import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts'
import { format, parseISO } from 'date-fns'
import { EmptyState } from './BarListWidget'
import type { MyAttendanceDay } from '@/api/attendance'

/** Clock time (local) as fractional hours, e.g. 9:30 → 9.5. */
function hourOfDay(iso: string): number {
  const d = new Date(iso)
  return d.getHours() + d.getMinutes() / 60
}

function fmtHour(h: number): string {
  const hh = Math.floor(h)
  const mm = Math.round((h - hh) * 60)
  const ampm = hh >= 12 ? 'PM' : 'AM'
  const h12 = hh % 12 === 0 ? 12 : hh % 12
  return `${h12}:${String(mm).padStart(2, '0')} ${ampm}`
}

/** "My Check In Trends": per day, the check-in time (line, left axis) and the
 *  hours worked (bars, right axis). Days without a check-in are skipped. */
export default function CheckinTrendChart({ days, height = 240 }: { days: MyAttendanceDay[]; height?: number }) {
  const data = days
    .filter((d) => d.checkin_time)
    .map((d) => ({
      label: format(parseISO(d.date), 'dd MMM'),
      checkin: Math.round(hourOfDay(d.checkin_time!) * 100) / 100,
      // An open session's "worked" grows until checkout — show 0 until it's closed.
      hours: d.is_active ? 0 : d.worked_hours,
    }))

  if (data.length === 0) return <EmptyState />

  const minIn = Math.floor(Math.min(...data.map((d) => d.checkin)))
  const maxIn = Math.ceil(Math.max(...data.map((d) => d.checkin)))

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} minTickGap={12} />
        <YAxis
          yAxisId="time"
          domain={[Math.max(0, minIn - 1), Math.min(24, maxIn + 1)]}
          tickFormatter={fmtHour}
          tick={{ fontSize: 11, fill: '#9ca3af' }}
          tickLine={false}
          axisLine={false}
          width={64}
        />
        <YAxis
          yAxisId="hours"
          orientation="right"
          tickFormatter={(v: number) => `${v}h`}
          tick={{ fontSize: 11, fill: '#9ca3af' }}
          tickLine={false}
          axisLine={false}
          width={36}
        />
        <Tooltip
          formatter={(value: number, name: string) =>
            name === 'Check-in time' ? fmtHour(value) : `${value}h`
          }
          contentStyle={{ borderRadius: 8, borderColor: '#e5e7eb', fontSize: 12 }}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar yAxisId="hours" dataKey="hours" name="Hours worked" fill="#c4b5fd" radius={[4, 4, 0, 0]} maxBarSize={18} />
        <Line yAxisId="time" type="monotone" dataKey="checkin" name="Check-in time" stroke="#EC5B2C" strokeWidth={2} dot={{ r: 2.5 }} />
      </ComposedChart>
    </ResponsiveContainer>
  )
}
